import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import { ApiError, api, setAuthProblemHandler } from "./api";
import type { MeResponse, Progress, Step, User } from "./types";

interface SessionState {
  /** False until the first `GET /api/auth/me` has come back. */
  loaded: boolean;
  user: User | null;
  step: Step;
  mfaEnrolled: boolean;
  settings: MeResponse["settings"];
  /** Set when /api/auth/me itself failed, so the shell can say so rather than hang. */
  error: string | null;
}

interface SessionValue extends SessionState {
  isAdmin: boolean;
  refresh: () => Promise<MeResponse | null>;
  /** Apply a known step change without a round trip (used right after login/MFA). */
  applyStep: (step: Exclude<Step, "none">, mfaEnrolled?: boolean) => void;
  signOut: () => Promise<void>;
  progress: Progress | null;
  setProgress: (progress: Progress) => void;
  reloadProgress: () => Promise<void>;
}

const FALLBACK_SETTINGS: MeResponse["settings"] = {
  allowSignup: false,
  brand: "The Terraformed Life",
  emailEnabled: false,
  sandboxEnabled: true,
  signInProviders: [],
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({
    loaded: false,
    user: null,
    step: "none",
    mfaEnrolled: false,
    settings: FALLBACK_SETTINGS,
    error: null,
  });
  const [progress, setProgressState] = useState<Progress | null>(null);

  const refresh = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const me = await api.me();
      setState({
        loaded: true,
        user: me.user,
        step: me.step,
        mfaEnrolled: me.mfaEnrolled,
        settings: me.settings ?? FALLBACK_SETTINGS,
        error: null,
      });
      if (!me.user) setProgressState(null);
      return me;
    } catch (error) {
      const message = error instanceof ApiError
        ? error.message
        : "We could not check your sign-in state.";
      setState((prev) => ({ ...prev, loaded: true, user: null, step: "none", error: message }));
      return null;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Any 401 from anywhere re-checks the session, which lets the route guards take over.
  useEffect(() => {
    let queued = false;
    setAuthProblemHandler(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        void refresh();
      }, 0);
    });
    return () => setAuthProblemHandler(null);
  }, [refresh]);

  const applyStep = useCallback((step: Exclude<Step, "none">, mfaEnrolled?: boolean) => {
    setState((prev) => ({
      ...prev,
      step,
      mfaEnrolled: mfaEnrolled ?? prev.mfaEnrolled,
    }));
    // The user record and settings only arrive from /me, so fetch them in the background.
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // A failed sign-out still clears the client; the cookie may already be gone.
    }
    setProgressState(null);
    setState({
      loaded: true,
      user: null,
      step: "none",
      mfaEnrolled: false,
      settings: state.settings,
      error: null,
    });
  }, [state.settings]);

  const reloadProgress = useCallback(async () => {
    try {
      setProgressState(await api.progress());
    } catch {
      // Progress is a nicety on most screens; the course map reloads it itself.
    }
  }, []);

  const value = useMemo<SessionValue>(() => ({
    ...state,
    isAdmin: state.user?.role === "admin",
    refresh,
    applyStep,
    signOut,
    progress,
    setProgress: setProgressState,
    reloadProgress,
  }), [state, refresh, applyStep, signOut, progress, reloadProgress]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider");
  return value;
}
